import { DEFAULT_INPUT_STATUS } from '~/constants/common';

import type { TypeInputState } from '~/types/common.types';
import type { TypeReceiveDetailFilterState, TypeReceiveRequestOrderInfoFilterErrorState } from '~/types/receive.types';

/**
 * [입고관리 > 입고요청관리] 입고요청등록 모달 > STEP 2 > 에러 상태 배열을 평탄화된 Map 형태로 변환하는 유틸 함수
 * @example key 예시 : "101.releaseCode", "101.kurlyDefault.address"
 */
export const flattenRegisterErrorList = (
  errorList: TypeReceiveRequestOrderInfoFilterErrorState[]
): Record<string, TypeInputState> => {
  const errorMap: Record<string, TypeInputState> = {};

  errorList.forEach((error) => {
    const orderKey = String(error.orderId);

    errorMap[`${orderKey}.releaseCode`] = error.releaseCode;
    errorMap[`${orderKey}.kurlyPickupAddress`] = error.kurlyPickupAddress;
    errorMap[`${orderKey}.milkrunPickupAddresses`] = error.milkrunPickupAddresses;

    (Object.keys(error.pickupAddressData) as Array<keyof typeof error.pickupAddressData>).forEach((addressKey) => {
      const addrErrorState = error.pickupAddressData[addressKey];

      errorMap[`${orderKey}.${addressKey}.address`] = addrErrorState.address;
      errorMap[`${orderKey}.${addressKey}.vehicleSize`] = addrErrorState.vehicleSize;
      errorMap[`${orderKey}.${addressKey}.palletQuantity`] = addrErrorState.palletQuantity;
    });

    const dateTimeErrorState: TypeInputState =
      error.expectedDate.status === 'error' || error.expectedTime.status === 'error'
        ? { status: 'error', message: error.expectedDate.message ?? error.expectedTime.message }
        : error.expectedDateTime;

    errorMap[`${orderKey}.expectedDateTime`] = dateTimeErrorState;
  });

  return errorMap;
};

/**
 * [입고관리 > 입고요청관리] 입고요청상세 모달 > 필터 에러 상태를 평탄화된 Map 형태로 변환하는 함수
 */
export const flattenDetailErrorState = (errorState: TypeReceiveDetailFilterState): Record<string, TypeInputState> => {
  const errorMap: Record<string, TypeInputState> = {};

  errorMap.releaseCode = errorState.releaseCode;
  errorMap.kurlyPickupAddress = errorState.kurlyPickupAddress;
  errorMap.milkrunPickupAddresses = errorState.milkrunPickupAddresses;

  (Object.keys(errorState.pickupAddressData) as Array<keyof typeof errorState.pickupAddressData>).forEach(
    (addressKey) => {
      const addrErrorState = errorState.pickupAddressData[addressKey];

      errorMap[`${addressKey}.address`] = addrErrorState.address;
      errorMap[`${addressKey}.vehicleSize`] = addrErrorState.vehicleSize;
      errorMap[`${addressKey}.palletQuantity`] = addrErrorState.palletQuantity;
    }
  );

  const dateTimeErrorState: TypeInputState =
    errorState.expectedDate.status === 'error' || errorState.expectedTime.status === 'error'
      ? { status: 'error', message: errorState.expectedDate.message ?? errorState.expectedTime.message }
      : DEFAULT_INPUT_STATUS;

  errorMap.expectedDateTime = dateTimeErrorState;

  return errorMap;
};
